import { readElementAnalyticsContext } from "./elementAttributes";
import { readHostConsentRelay } from "./hostConsent";
import type { AnalyticsConfig, PartnerAnalyticsContext } from "./types";

/** Analytics context the embedding page supplies, on the URL or in its markup. */
export type PartnerAnalyticsParams = Pick<
  AnalyticsConfig,
  "source" | "affiliateId" | "partnerGaAccount" | "partnerGtmAccount" | "hostConsent" | "hostConsentRelay"
>;

/** URL parameter carrying each piece of context, in the legacy widget's naming. */
const CONTEXT_PARAMS = {
  source: "source",
  affiliateId: "affiliate_id",
  partnerGaAccount: "ga_account",
  partnerGtmAccount: "gtm_account",
} as const satisfies Record<keyof PartnerAnalyticsContext, string>;

/** A parameter's value, or `null` when it is absent or blank. */
function readParam(params: URLSearchParams, name: string): string | null {
  const value = params.get(name)?.trim();

  return value === undefined || value === "" ? null : value;
}

/**
 * Reads the tracking context from a query string and from the custom element's
 * attributes.
 *
 * `ga_account`, `gtm_account` and `gtm_consent` keep the legacy widget's
 * parameter names so existing partner embeds continue to work unchanged.
 *
 * **A URL parameter wins over the same attribute.** The URL is our own hand-off
 * — Collins or the booking widget put those values there for this journey — while
 * an attribute is a static statement in the venue's page template, which cannot
 * know about the journey in hand. Where we handed something over, that is the
 * value; the attribute supplies what an inline embed's URL never carries, because
 * that URL belongs to the venue. See `docs/analytics.md`.
 *
 * `clientId`, which the widget also relays, is deliberately not read: see
 * `docs/consent.md`.
 *
 * @param search query string to read; defaults to the current page's.
 */
export function readPartnerAnalyticsParams(search: string = window.location.search): PartnerAnalyticsParams {
  const params = new URLSearchParams(search);
  const stated = readElementAnalyticsContext();
  const relay = readHostConsentRelay(search);

  return {
    source: readParam(params, CONTEXT_PARAMS.source) ?? stated.source,
    affiliateId: readParam(params, CONTEXT_PARAMS.affiliateId) ?? stated.affiliateId,
    partnerGaAccount: readParam(params, CONTEXT_PARAMS.partnerGaAccount) ?? stated.partnerGaAccount,
    partnerGtmAccount: readParam(params, CONTEXT_PARAMS.partnerGtmAccount) ?? stated.partnerGtmAccount,
    hostConsent: relay.categories,
    hostConsentRelay: relay,
  };
}
